/// <amd-module name="Wit/SprintsHub/SprintsView/Components/SprintViewOverflowDialog" />
import * as React from "react";
import { SprintPicker } from "./SprintPicker";
import { PeoplePicker } from "./Taskboard/PeoplePicker";
import { SprintDates } from "./SprintHeader/SprintDates";
import { Observer } from "VSSUI/Observer";
import { IVssComponentContext, IVssLayoutManager, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import * as Resources from "Wit/SprintsHub/SprintsView/Resources";
import { Dialog } from "VSSUI/Dialog";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { IMenuItem } from "VSSUI/Menu";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { IIterationData, ITeamSettings } from "Agile/Contracts/Common";
import { ITeamData, IProjectData } from "Tfs/Platform/Page";

export interface ISprintViewOverflowDialogProps {
    chartProps: IBurndownReportProps;
    iterationProps: IIterationProps;
    onDismiss: () => void;
    onShowLightBox: () => void;
    onPeoplePickerChanged: (peoplePickerSelection: DropdownSelection, peoplePickerItems: ObservableArray<IMenuItem>) => void;
    onSprintChanged: (iteration?: IIterationData | undefined, iterationIsChanged?: boolean | undefined) => void;
    onSprintDetailsChanged: (node: any) => Promise<void>;
    peoplePickerProps: IPeoplePickerProps;
    teamSettings: ITeamSettings;
}

interface IBurndownReportProps {
    teamData: ITeamData | undefined;
    projectData: IProjectData | undefined;
    chartWrapperRef: React.Ref<any>;
}

interface IPeoplePickerProps {
    peoplePickerSelection: DropdownSelection;
    peoplePickerItems: ObservableArray<IMenuItem>;
}

interface IIterationProps {
    selectedIteration: ObservableValue<IIterationData | undefined>;
    selectedIterationPath: ObservableValue<string>;
}

export class SprintViewOverflowDialog extends VssComponent<ISprintViewOverflowDialogProps> {
    private layoutManager: IVssLayoutManager;
    private iterationData: IIterationData;
    private iterationHasChanged: boolean | undefined;
    private peoplePickerSelection: DropdownSelection;
    private peoplePickerItems: ObservableArray<IMenuItem>;
    private peoplePickerRef = React.createRef<PeoplePicker>();

    constructor(props: ISprintViewOverflowDialogProps, context: IVssComponentContext) {
        super(props, context);
        this.layoutManager = context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        this.peoplePickerSelection = props.peoplePickerProps.peoplePickerSelection;
        this.peoplePickerItems = props.peoplePickerProps.peoplePickerItems;
    }

    public render(): JSX.Element | null {
        const { onDismiss, onSprintDetailsChanged, iterationProps, chartProps, teamSettings, onShowLightBox } = this.props;
        return (
            <Dialog
                lightDismiss={true}
                onDismiss={() => onDismiss()}
                titleProps={{ text: Resources.ViewOverflowSprintOptions }}
                footerButtonProps={[
                    {
                        text: Resources.Ok,
                        onClick: () => this.onSubmit(),
                        primary: true
                    },
                    {
                        text: Resources.Cancel,
                        onClick: () => onDismiss()
                    }
                ]}>
                <div className="sprints-header-dates overflow-dialog">
                    <div className="flex-column rhythm-vertical-8">
                        <SprintPicker key="sprint-picker-menu-button" onChange={this.onSprintPickerChange} />
                        <PeoplePicker key="people-picker-menu-button" ref={this.peoplePickerRef} selection={this.peoplePickerSelection} items={this.peoplePickerItems} />
                    </div>
                    <div className="flex-column rhythm-vertical-8">
                        <div key={"sprint-dates"} className="flex flex-column rhythm-horizontal-4 padding-top-16">
                            <SprintDates
                                selectedIteration={iterationProps.selectedIteration}
                                teamSettings={teamSettings!}
                                onSprintDetailsChanged={onSprintDetailsChanged}
                            />
                        </div>
                        <div key="burndown-report-div" onClick={onShowLightBox}>
                            <Observer
                                selectedIteration={iterationProps.selectedIteration}
                                iterationPath={iterationProps.selectedIterationPath}
                            >
                                {(props: {
                                    selectedIteration?: IIterationData;
                                    iterationPath: string;
                                }) => (
                                    <WrappedComponent
                                        key="burndown-report"
                                        wrappedType="BurndownReport"
                                        wrappedRef={chartProps.chartWrapperRef}
                                        dependencies={["ms.vss-reporting-web.burndown-report-content"]}
                                        wrappedProps={{
                                            teamId: chartProps.teamData?.id,
                                            teamName: chartProps.teamData?.name,
                                            projectId: chartProps.projectData?.id,
                                            iterationPath: props.iterationPath,
                                            iterationStartDate: props.selectedIteration?.startDate,
                                            iterationEndDate: props.selectedIteration?.finishDate,
                                            isMinimalChart: true,
                                            isLightbox: false,
                                            lastRefresh: new Date()
                                        }}
                                    />
                                )}
                            </Observer>
                        </div>
                    </div>
                </div>
            </Dialog>
        );
    }

    private onSprintPickerChange = (iterationData: IIterationData, iterationIsChanged: boolean | undefined): void => {
        this.iterationData = iterationData;
        this.iterationHasChanged = iterationIsChanged;
    }

    private onSubmit = (): void => {
        this.props.onPeoplePickerChanged(this.peoplePickerSelection, this.peoplePickerItems);
        this.props.onSprintChanged(this.iterationData, this.iterationHasChanged);
        this.props.onDismiss();
    }
}